/**
 * Lleva la pantalla al estado que pide cada paso de la ayuda tocando los
 * mismos botones que tocaría la persona (brújula, «Seleccionar», «Editar»,
 * la cifra de Asignado, «Mover dinero», el ⋯ de una cuenta). Solo abre,
 * elige y cierra: nunca cambia datos (el teclado se cierra con ✕, que no
 * guarda; las hojas, tocando su fondo).
 */
import type { Escena } from './pasos';

const SEL = {
  brujula: '[data-testid="brujula-opciones"]',
  opciones: '[data-testid="opciones-pantalla"]',
  seleccionar: '[data-testid="opcion-seleccionar"]',
  modoSeleccion: '[data-testid="modo-seleccion"]',
  cancelarModo: '[data-testid="modo-seleccion-cancelar"]',
  barra: '[data-testid="barra-seleccion"]',
  quitarSeleccion: '[data-testid="seleccion-quitar"]',
  editar: '[data-testid="seleccion-editar"]',
  hojaEditar: '[data-testid="hoja-editar-campos"]',
  cerrarHoja: '[data-testid="hoja-editar-campos"] [data-testid="hoja-cerrar"]',
  filasElegibles:
    '[data-ayuda-fila="normal"] button, [data-ayuda-fila="sin-categoria"] button',
  // Plan
  cifraAsignado:
    '[data-testid="category-row"] [data-testid="budget-cell-amount"]',
  teclado: '[data-testid="assign-keypad"]',
  cancelarTeclado: '[data-testid="keypad-cancelar"]',
  mover: '[data-testid="opcion-mover"]',
  hojaMover: '[data-testid="hoja-mover"]',
  cerrarMover: '[data-testid="mover-cerrar"]',
  // Inicio
  personalizarInicio: '[data-testid="inicio-personalizar"]',
  hechoInicio: '[data-testid="inicio-hecho"]',
  asaInicio: '[data-testid="inicio-asa"]',
  selectorTamano: '[data-testid="inicio-selector-tamano"]',
  cerrarSelectorTamano: '[data-testid="inicio-selector-cerrar"]',
  anadirTarjeta: '[data-testid="inicio-anadir-tarjeta"]',
  catalogo: '[data-testid="inicio-catalogo"]',
  cerrarCatalogo: '[data-testid="catalogo-cerrar"]',
  // Cuentas
  verEvolucion: '[data-testid="patrimonio-ver"]',
  menuDeCuenta: '[data-testid="cuenta-menu"]',
  hojaMenuCuenta: '[data-testid="menu-cuenta"]',
  anadirCuenta:
    '[data-testid="anadir-cuenta-enlace"], [data-testid="anadir-primera-cuenta"]',
  hojaNuevaCuenta: '[data-testid="nueva-cuenta"]',
  // Asignar el mes
  masOpciones: '[data-testid="auto-asignar-mas"]',
  hojaMasOpciones: '[data-testid="auto-asignar-opciones"]',
  filaAsignar: '[data-testid="asignar-fila-boton"]',
  barraGuardar: '[data-testid="asignar-barra-guardar"]',
  // Por revisar
  barraRevisar: '[data-testid="revisar-barra"]',
  casillaRevisar: '[data-testid="revisar-casilla"]',
  // Pagos fijos
  raizPagos: '[data-testid="anual"]',
  // Ficha de una categoría
  ficha: '[data-testid="ficha-disponible"]',
  vistaEvolucion: '[data-testid="evolucion-segmentado"] [aria-pressed="true"]',
  movsAlternar: '[data-testid="movimientos-alternar"]',
  editorObjetivo: '[data-testid="objetivo-editor"]',
  abrirEditor:
    '[data-testid="objetivo-resumen"], [data-testid="objetivo-anadir"]',
  cancelarEditor: '[data-testid="objetivo-cancelar"]',
  tipoObjetivo: '[data-testid^="objetivo-tipo-"][aria-pressed="true"]',
} as const;

export const SELECTORES_ESCENA = SEL;

function el(selector: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(selector);
}

function tocar(selector: string) {
  el(selector)?.click();
}

/** Espera (hasta `ms`) a que se cumpla `condicion`; devuelve si se cumplió. */
export function esperar(condicion: () => boolean, ms = 1500): Promise<boolean> {
  return new Promise(resolve => {
    const fin = Date.now() + ms;
    const mirar = () => {
      if (condicion()) {
        resolve(true);
      } else if (Date.now() > fin) {
        resolve(false);
      } else {
        window.setTimeout(mirar, 40);
      }
    };
    mirar();
  });
}

export function brujulaAbierta(): boolean {
  return el(SEL.brujula)?.getAttribute('aria-expanded') === 'true';
}

/** ¿Hay al menos dos movimientos que se puedan elegir? */
export function hayDosElegibles(): boolean {
  return document.querySelectorAll(SEL.filasElegibles).length >= 2;
}

async function abrirBrujula() {
  if (!brujulaAbierta()) {
    tocar(SEL.brujula);
  }
  await esperar(() => !!el(SEL.opciones));
}

async function cerrarBrujula() {
  if (brujulaAbierta()) {
    tocar(SEL.brujula);
  }
  await esperar(() => !el(SEL.opciones));
}

async function quitarSeleccion() {
  tocar(SEL.quitarSeleccion);
  tocar(SEL.cancelarModo);
  await esperar(() => !el(SEL.barra) && !el(SEL.modoSeleccion));
}

async function elegirDos() {
  if (el(SEL.barra)) {
    return;
  }
  if (!el(SEL.modoSeleccion)) {
    await abrirBrujula();
    tocar(SEL.seleccionar);
    await esperar(() => !!el(SEL.modoSeleccion) && !el(SEL.opciones));
  }
  const filas = [
    ...document.querySelectorAll<HTMLElement>(SEL.filasElegibles),
  ].slice(0, 2);
  for (const fila of filas) {
    fila.click();
  }
  await esperar(() => !!el(SEL.barra));
}

async function cerrarEditar() {
  tocar(SEL.cerrarHoja);
  await esperar(() => !el(SEL.hojaEditar));
}

async function cerrarTeclado() {
  tocar(SEL.cancelarTeclado);
  await esperar(() => !el(SEL.teclado));
}

async function abrirTeclado() {
  if (!el(SEL.teclado)) {
    tocar(SEL.cifraAsignado);
    await esperar(() => !!el(SEL.teclado));
  }
}

async function cerrarMover() {
  tocar(SEL.cerrarMover);
  await esperar(() => !el(SEL.hojaMover));
}

async function abrirMover() {
  if (!el(SEL.hojaMover)) {
    await abrirBrujula();
    tocar(SEL.mover);
    await esperar(() => !!el(SEL.hojaMover));
  }
}

/**
 * Inicio: personalizar, el selector de tamaño y el catálogo. Solo abre y
 * cierra; nunca mueve, cambia ni quita tarjetas.
 */
async function escenaInicio(destino: Escena) {
  if (destino !== 'inicio-catalogo' && el(SEL.catalogo)) {
    tocar(SEL.cerrarCatalogo);
    await esperar(() => !el(SEL.catalogo));
  }
  if (destino !== 'inicio-tamano' && el(SEL.selectorTamano)) {
    tocar(SEL.cerrarSelectorTamano);
    await esperar(() => !el(SEL.selectorTamano));
  }
  const editar = destino.startsWith('inicio-');
  if (!editar && el(SEL.hechoInicio)) {
    tocar(SEL.hechoInicio);
    await esperar(() => !el(SEL.hechoInicio));
  }
  if (!editar) {
    return;
  }
  await cerrarBrujula();
  if (!el(SEL.hechoInicio)) {
    tocar(SEL.personalizarInicio);
    await esperar(() => !!el(SEL.hechoInicio));
  }
  if (destino === 'inicio-tamano' && !el(SEL.selectorTamano)) {
    tocar(SEL.asaInicio);
    await esperar(() => !!el(SEL.selectorTamano));
  }
  if (destino === 'inicio-catalogo' && !el(SEL.catalogo)) {
    el(SEL.anadirTarjeta)?.scrollIntoView({ block: 'center' });
    tocar(SEL.anadirTarjeta);
    await esperar(() => !!el(SEL.catalogo));
  }
}

/**
 * Cierra una hoja tocando su fondo oscuro, como la persona: la hoja solo
 * cierra un toque que empieza en el fondo, así que primero «pointerdown».
 */
async function cerrarHojaPorFondo(selector: string) {
  const fondo = el(selector)?.parentElement;
  if (!fondo) {
    return;
  }
  fondo.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true }));
  fondo.click();
  await esperar(() => !el(selector));
}

/** «Ver evolución» lo abre la ayuda y lo vuelve a cerrar ella. */
let evolucionAbiertaPorAyuda = false;

/**
 * Cuentas: el menú ⋯ de la primera cuenta, la hoja «Añadir cuenta» y «Ver
 * evolución». Nunca pulsa una opción del menú ni «Añadir cuenta» de la hoja.
 */
async function escenaCuentas(destino: Escena) {
  if (destino !== 'menu-cuenta' && el(SEL.hojaMenuCuenta)) {
    await cerrarHojaPorFondo(SEL.hojaMenuCuenta);
  }
  if (destino !== 'nueva-cuenta' && el(SEL.hojaNuevaCuenta)) {
    await cerrarHojaPorFondo(SEL.hojaNuevaCuenta);
  }
  const ver = el(SEL.verEvolucion);
  const abierta = ver?.getAttribute('aria-expanded') === 'true';
  if (destino === 'patrimonio' && ver && !abierta) {
    ver.click();
    evolucionAbiertaPorAyuda = true;
    await esperar(() => ver.getAttribute('aria-expanded') === 'true');
  } else if (destino !== 'patrimonio' && evolucionAbiertaPorAyuda) {
    evolucionAbiertaPorAyuda = false;
    if (ver && abierta) {
      ver.click();
      await esperar(() => ver.getAttribute('aria-expanded') !== 'true');
    }
  }
  if (destino === 'menu-cuenta' && !el(SEL.hojaMenuCuenta)) {
    await cerrarBrujula();
    tocar(SEL.menuDeCuenta);
    await esperar(() => !!el(SEL.hojaMenuCuenta));
  }
  if (destino === 'nueva-cuenta' && !el(SEL.hojaNuevaCuenta)) {
    await cerrarBrujula();
    tocar(SEL.anadirCuenta);
    await esperar(() => !!el(SEL.hojaNuevaCuenta));
  }
}

/** Fila de Ajustes que abrió la ayuda y la que tenía abierta la persona. */
let filaDeAyuda: string | null = null;
let filaDeAntes: string | null = null;

function filaAjusteAbierta(): string | null {
  const boton = el('[data-testid^="ajuste-"][aria-expanded="true"]');
  return boton?.dataset.testid?.slice('ajuste-'.length) ?? null;
}

async function tocarFila(id: string, abierta: boolean) {
  tocar(`[data-testid="ajuste-${id}"]`);
  await esperar(
    () => !!el(`[data-testid="ajuste-${id}-contenido"]`) === abierta,
  );
}

/**
 * Ajustes: despliega la fila del paso tocando su cabecera, como la persona.
 * Solo hay una abierta a la vez, así que al terminar la ayuda vuelve a dejar
 * abierta la que tuviera la persona (o ninguna). Nunca toca nada de dentro.
 */
async function escenaAjustes(destino: Escena) {
  const abierta = filaAjusteAbierta();
  if (destino.startsWith('ajuste-')) {
    const id = destino.slice('ajuste-'.length);
    if (abierta !== id) {
      if (filaDeAyuda == null) {
        filaDeAntes = abierta;
      }
      filaDeAyuda = id;
      await tocarFila(id, true);
    }
    return;
  }
  if (filaDeAyuda == null) {
    return;
  }
  const deAyuda = filaDeAyuda;
  const deAntes = filaDeAntes;
  filaDeAyuda = null;
  filaDeAntes = null;
  if (deAntes && abierta !== deAntes) {
    await tocarFila(deAntes, true);
  } else if (!deAntes && abierta === deAyuda) {
    await tocarFila(deAyuda, false);
  }
}

/**
 * La barra «Guardar presupuesto» de mentira: Asignar el mes la pinta (sin
 * poder pulsarse) mientras la ayuda la enseña y no hay cambios de verdad.
 */
let fantasmaGuardar = false;
const avisosFantasma = new Set<() => void>();

function ponerFantasmaGuardar(valor: boolean) {
  if (fantasmaGuardar !== valor) {
    fantasmaGuardar = valor;
    avisosFantasma.forEach(avisar => avisar());
  }
}

export function suscribirFantasmaGuardar(avisar: () => void): () => void {
  avisosFantasma.add(avisar);
  return () => {
    avisosFantasma.delete(avisar);
  };
}

export function hayFantasmaGuardar(): boolean {
  return fantasmaGuardar;
}

/**
 * Asignar el mes: la hoja «Más opciones…», el teclado de la primera categoría
 * y la barra de guardar. Nunca pulsa una opción, «Hecho» ni «Guardar»: el
 * teclado se cierra con ✕ y la hoja, tocando su fondo.
 */
async function escenaAsignar(destino: Escena) {
  if (destino !== 'asignar-mas' && el(SEL.hojaMasOpciones)) {
    await cerrarHojaPorFondo(SEL.hojaMasOpciones);
  }
  if (destino !== 'asignar-teclado' && el(SEL.filaAsignar) && el(SEL.teclado)) {
    await cerrarTeclado();
  }
  if (destino !== 'asignar-guardar') {
    ponerFantasmaGuardar(false);
  }
  if (!destino.startsWith('asignar-')) {
    return;
  }
  await cerrarBrujula();
  if (destino === 'asignar-mas' && !el(SEL.hojaMasOpciones)) {
    tocar(SEL.masOpciones);
    // Las cifras de los meses anteriores tardan un poco («…» mientras).
    await esperar(
      () =>
        !!el(SEL.hojaMasOpciones) &&
        !/…/.test(el(SEL.hojaMasOpciones)?.textContent ?? ''),
      3000,
    );
  }
  if (destino === 'asignar-teclado' && !el(SEL.teclado)) {
    tocar(SEL.filaAsignar);
    await esperar(() => !!el(SEL.teclado));
  }
  if (destino === 'asignar-guardar' && !el(SEL.barraGuardar)) {
    ponerFantasmaGuardar(true);
    await esperar(() => !!el(SEL.barraGuardar));
  }
}

/** Fila de «Por revisar» cuya casilla marcó la ayuda (para desmarcarla). */
let filaMarcadaPorAyuda: string | null = null;

function casillaDe(id: string): HTMLElement | null {
  return el(
    `[data-testid="revisar-fila"][data-id="${CSS.escape(id)}"] ${SEL.casillaRevisar}`,
  );
}

/**
 * Por revisar: para enseñar la barra de abajo marca UNA casilla (solo
 * selecciona: no aprueba ni cambia nada) y al salir de la escena la desmarca.
 * Si la persona ya tenía algo marcado, no toca nada.
 */
async function escenaRevisar(destino: Escena) {
  if (destino === 'revisar-seleccion') {
    await cerrarBrujula();
    if (el(SEL.barraRevisar)) {
      return;
    }
    const casilla = el(`${SEL.casillaRevisar}[aria-pressed="false"]`);
    const fila = casilla?.closest<HTMLElement>('[data-testid="revisar-fila"]');
    if (!casilla || !fila?.dataset.id) {
      return;
    }
    filaMarcadaPorAyuda = fila.dataset.id;
    casilla.click();
    await esperar(() => !!el(SEL.barraRevisar));
    return;
  }
  if (filaMarcadaPorAyuda == null) {
    return;
  }
  const casilla = casillaDe(filaMarcadaPorAyuda);
  filaMarcadaPorAyuda = null;
  if (casilla?.getAttribute('aria-pressed') === 'true') {
    casilla.click();
    await esperar(() => casilla.getAttribute('aria-pressed') !== 'true');
  }
}

/** Pestaña de Pagos fijos que tenía la persona antes de que la ayuda la cambiara. */
let pestanaDeAntes: string | null = null;

async function ponerPestanaPagos(pestana: string) {
  const raiz = el(SEL.raizPagos);
  if (!raiz || raiz.dataset.pestana === pestana) {
    return;
  }
  tocar(`[data-testid="pagos-fijos-pestana-${pestana}"]`);
  await esperar(() => el(SEL.raizPagos)?.dataset.pestana === pestana, 2500);
  // La lista nueva carga un instante después del cambio de pestaña.
  await esperar(() => !el(`${SEL.raizPagos} [data-testid="cargando"]`), 2500);
}

/**
 * Pagos fijos: cambia a «Mensuales» o «Anuales» tocando su botón, como la
 * persona (solo es la vista; no cambia datos), y al acabar deja la que
 * tenía.
 */
async function escenaPagos(destino: Escena) {
  const raiz = el(SEL.raizPagos);
  if (!raiz) {
    pestanaDeAntes = null;
    return;
  }
  if (destino === 'pagos-mensuales' || destino === 'pagos-anuales') {
    if (pestanaDeAntes == null) {
      pestanaDeAntes = raiz.dataset.pestana ?? null;
    }
    await ponerPestanaPagos(
      destino === 'pagos-mensuales' ? 'mensuales' : 'anuales',
    );
    return;
  }
  if (pestanaDeAntes != null) {
    const antes = pestanaDeAntes;
    pestanaDeAntes = null;
    await ponerPestanaPagos(antes);
  }
}

/**
 * Lo que tenía la ficha antes de que la ayuda tocara «Evolución» (vista y
 * «Ver movimientos») y si el editor del objetivo lo abrió ella (y en qué
 * pestaña de tipo estaba).
 */
let evolucionDeAntes: { vista: string | null; movs: boolean } | null = null;
let editorAbiertoPorAyuda = false;
let tipoDeAntes: string | null = null;
let cicloTipos = 0;

const TIPOS_OBJETIVO = ['mensual', 'semanal', 'anual', 'una-vez'] as const;

function testidSin(selector: string, prefijo: string): string | null {
  return el(selector)?.dataset.testid?.slice(prefijo.length) ?? null;
}

function movsAbiertos(): boolean {
  return el(SEL.movsAlternar)?.getAttribute('aria-expanded') === 'true';
}

async function ponerVista(vista: string) {
  if (testidSin(SEL.vistaEvolucion, 'evolucion-') === vista) {
    return;
  }
  tocar(`[data-testid="evolucion-${vista}"]`);
  await esperar(() => testidSin(SEL.vistaEvolucion, 'evolucion-') === vista);
}

async function ponerMovs(abiertos: boolean) {
  if (!el(SEL.movsAlternar) || movsAbiertos() === abiertos) {
    return;
  }
  tocar(SEL.movsAlternar);
  await esperar(() => movsAbiertos() === abiertos);
}

async function ponerTipo(tipo: string) {
  if (testidSin(SEL.tipoObjetivo, 'objetivo-tipo-') === tipo) {
    return;
  }
  tocar(`[data-testid="objetivo-tipo-${tipo}"]`);
  await esperar(() => testidSin(SEL.tipoObjetivo, 'objetivo-tipo-') === tipo);
}

function pararCicloTipos() {
  window.clearInterval(cicloTipos);
  cicloTipos = 0;
}

/**
 * Ficha de una categoría: cambia la vista de «Evolución», despliega «Ver
 * movimientos» y abre el editor del objetivo, como la persona (solo es la
 * vista o un formulario sin guardar). Al salir lo deja como estaba: la vista
 * y el despliegue de antes, y el editor cerrado con «Cancelar». Si el editor
 * ya lo tenía abierto la persona, no cambia su pestaña ni lo cierra.
 */
async function escenaFicha(destino: Escena) {
  pararCicloTipos();
  if (!el(SEL.ficha)) {
    evolucionDeAntes = null;
    editorAbiertoPorAyuda = false;
    tipoDeAntes = null;
    return;
  }
  const enEditor = destino.startsWith('ficha-editor');
  const enEvolucion = destino.startsWith('ficha-') && !enEditor;
  if (enEditor || enEvolucion) {
    await cerrarBrujula();
  }

  if (enEditor) {
    if (!el(SEL.editorObjetivo) && el(SEL.abrirEditor)) {
      tocar(SEL.abrirEditor);
      editorAbiertoPorAyuda = true;
      await esperar(() => !!el(SEL.editorObjetivo));
      tipoDeAntes = testidSin(SEL.tipoObjetivo, 'objetivo-tipo-');
    }
    if (editorAbiertoPorAyuda) {
      if (destino === 'ficha-editor-una-vez') {
        await ponerTipo('una-vez');
      } else if (tipoDeAntes) {
        await ponerTipo(tipoDeAntes);
      }
      if (destino === 'ficha-editor-tipos') {
        let i = TIPOS_OBJETIVO.findIndex(t => t === tipoDeAntes);
        cicloTipos = window.setInterval(() => {
          i = (i + 1) % TIPOS_OBJETIVO.length;
          tocar(`[data-testid="objetivo-tipo-${TIPOS_OBJETIVO[i]}"]`);
        }, 1400);
      }
    }
  } else if (editorAbiertoPorAyuda) {
    editorAbiertoPorAyuda = false;
    tipoDeAntes = null;
    if (el(SEL.editorObjetivo)) {
      tocar(SEL.cancelarEditor);
      await esperar(() => !el(SEL.editorObjetivo));
    }
  }

  if (enEvolucion) {
    if (evolucionDeAntes == null) {
      evolucionDeAntes = {
        vista: testidSin(SEL.vistaEvolucion, 'evolucion-'),
        movs: movsAbiertos(),
      };
    }
    if (destino === 'ficha-movimientos') {
      await ponerMovs(true);
    } else {
      await ponerVista(destino.slice('ficha-'.length));
      await ponerMovs(evolucionDeAntes.movs);
    }
  } else if (evolucionDeAntes != null) {
    const antes = evolucionDeAntes;
    evolucionDeAntes = null;
    if (antes.vista) {
      await ponerVista(antes.vista);
    }
    await ponerMovs(antes.movs);
  }
}

/** Lleva la pantalla a `destino` desde donde esté. */
export async function llevarAEscena(destino: Escena): Promise<void> {
  if (destino === 'propia') {
    return;
  }
  await escenaAjustes(destino);
  if (destino.startsWith('ajuste-')) {
    return;
  }
  await escenaAsignar(destino);
  if (destino.startsWith('asignar-')) {
    return;
  }
  await escenaInicio(destino);
  if (destino.startsWith('inicio-')) {
    return;
  }
  await escenaRevisar(destino);
  if (destino === 'revisar-seleccion') {
    return;
  }
  await escenaPagos(destino);
  await escenaFicha(destino);
  if (destino.startsWith('ficha-')) {
    return;
  }
  await escenaCuentas(destino);
  if (
    destino === 'patrimonio' ||
    destino === 'menu-cuenta' ||
    destino === 'nueva-cuenta'
  ) {
    return;
  }
  if (destino !== 'mover' && el(SEL.hojaMover)) {
    await cerrarMover();
  }
  if (destino !== 'teclado' && el(SEL.teclado)) {
    await cerrarTeclado();
  }
  if (destino === 'mover') {
    await abrirMover();
    return;
  }
  if (destino !== 'editar' && el(SEL.hojaEditar)) {
    await cerrarEditar();
  }
  if (
    (destino === 'normal' || destino === 'brujula') &&
    (el(SEL.barra) || el(SEL.modoSeleccion))
  ) {
    await quitarSeleccion();
  }
  if (destino === 'brujula') {
    await abrirBrujula();
  } else {
    await cerrarBrujula();
  }
  if (destino === 'seleccion' || destino === 'editar') {
    await elegirDos();
  }
  if (destino === 'editar' && !el(SEL.hojaEditar)) {
    tocar(SEL.editar);
    await esperar(() => !!el(SEL.hojaEditar));
  }
  if (destino === 'teclado') {
    await abrirTeclado();
  }
}
